/**
 * 🏛️ EXCELÊNCIA STORE — Zustand Global State
 *
 * Store centralizado do módulo de Governança de Processos.
 * Gerencia todo o estado de blueprints, KPIs, filtros e UI.
 *
 * Camadas:
 *   Domain → blueprints, kpis, usageCounts
 *   Application → mutations (create, publish, archive, etc.)
 *   UI → filters, search, toast, processing, modals
 *
 * Regras:
 *  - Todas as queries filtram por organization_id
 *  - Mutations chamam reload() para manter KPIs sincronizados
 *  - Toast e confirmação são gerenciados aqui (não em componentes)
 */

import { create } from 'zustand';
import {
  listBlueprints,
  deleteBlueprint,
} from '../services/processBlueprintService';
import {
  publishBlueprint,
  cloneBlueprint,
  createVersion,
  generateWorkflowFromBlueprint,
  generateMapFromBlueprint,
  getExcelenciaKPIs,
  archiveBlueprint,
  createManualBlueprint,
  editBlueprint,
  reactivateBlueprint,
  setAsStandard,
  removeStandard,
  listAvailableMaps,
  listAvailableWorkflows,
  createBlueprintFromMap,
  createBlueprintFromWorkflow,
} from '../services/excelenciaService';
import type {
  MapSourceItem,
  WorkflowSourceItem,
} from '../services/excelenciaService';
import {
  listVersions,
  listUsageByBlueprint,
  getUsageCounts,
  linkBlueprintToProject,
  unlinkBlueprintFromProject,
} from '../services/blueprintVersionUsageService';
import { listBlueprintLogs } from '../services/blueprintLogService';
import type {
  ProcessBlueprint,
  ExcelenciaKPIs,
  BlueprintUsageCount,
  BlueprintCategory,
  BlueprintStatus,
  ProcessBlueprintVersion,
  ProcessBlueprintUsage,
  ProcessBlueprintLog,
  BlueprintStage,
} from '../../../types/processBlueprint';

// ============================================================================
// TYPES
// ============================================================================

export type ConfirmAction =
  | 'publish'
  | 'archive'
  | 'delete'
  | 'setStandard'
  | 'removeStandard';

export type FilterStatus = 'all' | BlueprintStatus;
export type SortKey = 'name' | 'status' | 'semver' | 'usage' | 'category' | 'updatedAt';
export type SortDir = 'asc' | 'desc';

export interface Toast {
  type: 'success' | 'error';
  message: string;
}

export interface ConfirmState {
  action: ConfirmAction;
  blueprint: ProcessBlueprint;
}

interface ExcelenciaState {
  // ── Domain ──
  organizationId: string;
  blueprints: ProcessBlueprint[];
  kpis: ExcelenciaKPIs | null;
  usageCounts: BlueprintUsageCount[];

  // ── Data loading ──
  isLoading: boolean;
  error: Error | null;

  // ── UI: Filters ──
  search: string;
  filterStatus: FilterStatus;
  sortKey: SortKey;
  sortDir: SortDir;

  // ── UI: Processing ──
  processingId: string | null;
  toast: Toast | null;

  // ── UI: Modals ──
  confirmState: ConfirmState | null;
  confirmLoading: boolean;
  showCreateModal: boolean;
  editTarget: ProcessBlueprint | null;
  versionTarget: ProcessBlueprint | null;
  logTarget: ProcessBlueprint | null;
  usageTarget: ProcessBlueprint | null;
  usageData: ProcessBlueprintUsage[];
  usageLoading: boolean;

  // ── Actions: Init ──
  setOrganizationId: (id: string) => void;
  reload: () => Promise<void>;

  // ── Actions: Filters ──
  setSearch: (q: string) => void;
  setFilterStatus: (s: FilterStatus) => void;
  toggleSort: (key: SortKey) => void;

  // ── Actions: Toast ──
  setToast: (t: Toast | null) => void;
  clearToast: () => void;

  // ── Actions: Modals ──
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (bp: ProcessBlueprint) => void;
  closeEditModal: () => void;
  openVersionPanel: (bp: ProcessBlueprint) => void;
  closeVersionPanel: () => void;
  openLogPanel: (bp: ProcessBlueprint) => void;
  closeLogPanel: () => void;
  openUsagePanel: (bp: ProcessBlueprint) => Promise<void>;
  closeUsagePanel: () => void;

  // ── Actions: Confirm ──
  requestConfirm: (action: ConfirmAction, bp: ProcessBlueprint) => void;
  cancelConfirm: () => void;
  executeConfirm: (userId?: string) => Promise<void>;

  // ── Actions: Mutations ──
  createManual: (params: {
    name: string;
    description?: string;
    category?: BlueprintCategory;
    stages?: BlueprintStage[];
    projectId?: string | null;
    createdBy?: string;
  }) => Promise<ProcessBlueprint>;
  createFromMap: (params: {
    name: string;
    description?: string;
    category?: BlueprintCategory;
    mapId: string;
    createdBy?: string;
  }) => Promise<ProcessBlueprint>;
  createFromWorkflow: (params: {
    name: string;
    description?: string;
    category?: BlueprintCategory;
    templateId: string;
    createdBy?: string;
  }) => Promise<ProcessBlueprint>;
  cloneBlueprint: (id: string, newName?: string) => Promise<void>;
  createNewVersion: (id: string, changeSummary?: string, createdBy?: string) => Promise<void>;
  editBlueprint: (
    id: string,
    changes: { name?: string; description?: string; category?: BlueprintCategory; stages?: BlueprintStage[] },
    changeSummary?: string,
    editedBy?: string,
  ) => Promise<void>;
  genWorkflow: (id: string, createdBy?: string) => Promise<void>;
  genMap: (id: string) => Promise<void>;
  reactivate: (id: string) => Promise<void>;

  // ── Actions: Import sources ──
  loadAvailableMaps: (userId: string) => Promise<MapSourceItem[]>;
  loadAvailableWorkflows: () => Promise<WorkflowSourceItem[]>;

  // ── Actions: Versions / Usage / Logs (lazy) ──
  loadVersions: (blueprintId: string) => Promise<ProcessBlueprintVersion[]>;
  loadUsage: (blueprintId: string) => Promise<ProcessBlueprintUsage[]>;
  loadLogs: (blueprintId: string) => Promise<ProcessBlueprintLog[]>;
  linkToProject: (blueprintId: string, projectId: string, linkedBy?: string) => Promise<void>;
  unlinkFromProject: (blueprintId: string, projectId: string) => Promise<void>;
}

// ============================================================================
// HELPERS
// ============================================================================

const CONFIRM_LABELS: Record<ConfirmAction, { label: string; fn: string }> = {
  publish: { label: 'Blueprint publicado', fn: 'publish' },
  archive: { label: 'Blueprint arquivado', fn: 'archive' },
  delete: { label: 'Blueprint excluído', fn: 'delete' },
  setStandard: { label: 'Marcado como padrão corporativo', fn: 'setStandard' },
  removeStandard: { label: 'Padrão corporativo removido', fn: 'removeStandard' },
};

// ============================================================================
// STORE
// ============================================================================

export const useExcelenciaStore = create<ExcelenciaState>((set, get) => ({
  // ── Domain ──
  organizationId: '',
  blueprints: [],
  kpis: null,
  usageCounts: [],

  // ── Data loading ──
  isLoading: false,
  error: null,

  // ── UI: Filters ──
  search: '',
  filterStatus: 'all',
  sortKey: 'updatedAt',
  sortDir: 'desc',

  // ── UI: Processing ──
  processingId: null,
  toast: null,

  // ── UI: Modals ──
  confirmState: null,
  confirmLoading: false,
  showCreateModal: false,
  editTarget: null,
  versionTarget: null,
  logTarget: null,
  usageTarget: null,
  usageData: [],
  usageLoading: false,

  // ══════════════════════════════════════════════════════════════
  // ACTIONS
  // ══════════════════════════════════════════════════════════════

  setOrganizationId: (id) => {
    if (id !== get().organizationId) {
      set({ organizationId: id });
    }
  },

  reload: async () => {
    const { organizationId } = get();
    if (!organizationId) return;
    set({ isLoading: true, error: null });
    try {
      const [data, kpiData, usageData] = await Promise.all([
        listBlueprints(organizationId),
        getExcelenciaKPIs(organizationId),
        getUsageCounts(organizationId).catch(() => [] as BlueprintUsageCount[]),
      ]);
      set({ blueprints: data, kpis: kpiData, usageCounts: usageData });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as any).message)
            : 'Erro ao carregar blueprints';
      console.error('[ExcelenciaStore] reload error:', err);
      set({ error: new Error(message) });
    } finally {
      set({ isLoading: false });
    }
  },

  // ── Filters ──

  setSearch: (q) => set({ search: q }),
  setFilterStatus: (s) => set({ filterStatus: s }),
  toggleSort: (key) => {
    const { sortKey, sortDir } = get();
    if (sortKey === key) {
      set({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      set({ sortKey: key, sortDir: 'asc' });
    }
  },

  // ── Toast ──

  setToast: (t) => set({ toast: t }),
  clearToast: () => set({ toast: null }),

  // ── Modals ──

  openCreateModal: () => set({ showCreateModal: true }),
  closeCreateModal: () => set({ showCreateModal: false }),
  openEditModal: (bp) => set({ editTarget: bp }),
  closeEditModal: () => set({ editTarget: null }),
  openVersionPanel: (bp) => set({ versionTarget: bp }),
  closeVersionPanel: () => set({ versionTarget: null }),
  openLogPanel: (bp) => set({ logTarget: bp }),
  closeLogPanel: () => set({ logTarget: null }),

  openUsagePanel: async (bp) => {
    set({ usageTarget: bp, usageLoading: true, usageData: [] });
    try {
      const data = await listUsageByBlueprint(bp.id);
      set({ usageData: data });
    } catch {
      set({ usageData: [] });
    } finally {
      set({ usageLoading: false });
    }
  },
  closeUsagePanel: () => set({ usageTarget: null, usageData: [] }),

  // ── Confirm ──

  requestConfirm: (action, bp) => set({ confirmState: { action, blueprint: bp } }),
  cancelConfirm: () => {
    if (!get().confirmLoading) set({ confirmState: null });
  },

  executeConfirm: async (userId) => {
    const { confirmState, organizationId } = get();
    if (!confirmState) return;
    const { action, blueprint } = confirmState;
    set({ confirmLoading: true, processingId: blueprint.id });

    const { label } = CONFIRM_LABELS[action];
    try {
      switch (action) {
        case 'publish':
          await publishBlueprint(blueprint.id, userId);
          break;
        case 'archive':
          await archiveBlueprint(blueprint.id);
          break;
        case 'delete':
          await deleteBlueprint(blueprint.id);
          break;
        case 'setStandard':
          await setAsStandard(blueprint.id);
          break;
        case 'removeStandard':
          await removeStandard(blueprint.id);
          break;
      }
      set({ toast: { type: 'success', message: `${label} — "${blueprint.name}"` } });
      // Reload to refresh KPIs
      await get().reload();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : `Erro: ${label}`;
      set({ toast: { type: 'error', message } });
    } finally {
      set({ confirmLoading: false, confirmState: null, processingId: null });
    }
  },

  // ── Mutations ──

  createManual: async (params) => {
    const { organizationId } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    const created = await createManualBlueprint({ ...params, organizationId });
    await get().reload();
    return created;
  },

  createFromMap: async (params) => {
    const { organizationId } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    const created = await createBlueprintFromMap({ ...params, organizationId });
    await get().reload();
    return created;
  },

  createFromWorkflow: async (params) => {
    const { organizationId } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    const created = await createBlueprintFromWorkflow({ ...params, organizationId });
    await get().reload();
    return created;
  },

  cloneBlueprint: async (id, newName) => {
    const { organizationId, reload: r } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    const bp = get().blueprints.find(b => b.id === id);
    set({ processingId: id });
    try {
      await cloneBlueprint(id, organizationId, newName);
      set({ toast: { type: 'success', message: `Blueprint clonado — "${bp?.name ?? id}"` } });
      await r();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao clonar';
      set({ toast: { type: 'error', message: msg } });
    } finally {
      set({ processingId: null });
    }
  },

  createNewVersion: async (id, changeSummary, createdBy) => {
    const { organizationId } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    const bp = get().blueprints.find(b => b.id === id);
    set({ processingId: id });
    try {
      await createVersion(id, organizationId, changeSummary ?? '', 'patch');
      set({
        toast: { type: 'success', message: `Nova versão criada — "${bp?.name ?? id}"` },
        versionTarget: null,
      });
      await get().reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao versionar';
      set({ toast: { type: 'error', message: msg } });
    } finally {
      set({ processingId: null });
    }
  },

  editBlueprint: async (id, changes, changeSummary, editedBy) => {
    set({ processingId: id });
    try {
      await editBlueprint(id, changes, editedBy, changeSummary);
      set({
        toast: { type: 'success', message: 'Blueprint atualizado com sucesso' },
        editTarget: null,
      });
      await get().reload();
    } catch (err: unknown) {
      throw err; // Propagate to modal
    } finally {
      set({ processingId: null });
    }
  },

  genWorkflow: async (id, createdBy) => {
    const bp = get().blueprints.find(b => b.id === id);
    set({ processingId: id });
    try {
      await generateWorkflowFromBlueprint(id, createdBy);
      set({ toast: { type: 'success', message: `Workflow gerado — "${bp?.name ?? id}"` } });
      await get().reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao gerar workflow';
      set({ toast: { type: 'error', message: msg } });
    } finally {
      set({ processingId: null });
    }
  },

  genMap: async (id) => {
    const bp = get().blueprints.find(b => b.id === id);
    set({ processingId: id });
    try {
      await generateMapFromBlueprint(id);
      set({ toast: { type: 'success', message: `Mapa gerado — "${bp?.name ?? id}"` } });
      await get().reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao gerar mapa';
      set({ toast: { type: 'error', message: msg } });
    } finally {
      set({ processingId: null });
    }
  },

  reactivate: async (id) => {
    const bp = get().blueprints.find(b => b.id === id);
    set({ processingId: id });
    try {
      await reactivateBlueprint(id);
      set({ toast: { type: 'success', message: `Blueprint reativado — "${bp?.name ?? id}"` } });
      await get().reload();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao reativar';
      set({ toast: { type: 'error', message: msg } });
    } finally {
      set({ processingId: null });
    }
  },

  // ── Import sources ──

  loadAvailableMaps: async (userId) => listAvailableMaps(userId),
  loadAvailableWorkflows: async () => {
    const { organizationId } = get();
    if (!organizationId) return [];
    return listAvailableWorkflows(organizationId);
  },

  // ── Versions / Usage / Logs (lazy) ──

  loadVersions: (blueprintId) => listVersions(blueprintId),
  loadUsage: (blueprintId) => listUsageByBlueprint(blueprintId),
  loadLogs: (blueprintId) => listBlueprintLogs(blueprintId),

  linkToProject: async (blueprintId, projectId, linkedBy) => {
    const { organizationId } = get();
    if (!organizationId) throw new Error('organizationId é obrigatório');
    await linkBlueprintToProject({ blueprintId, projectId, organizationId, linkedBy });
    await get().reload();
  },
  unlinkFromProject: async (blueprintId, projectId) => {
    await unlinkBlueprintFromProject(blueprintId, projectId);
    await get().reload();
  },
}));
